/**
 * The flows that open on top of the New Connection dialog — Import from another tool (and its Azure review) and Detect
 * local databases (Option C, C3, D9) — and what their completion does to the dialog under them.
 *
 * While mounted in create mode the dialog is the registered host (`connectionDialogHostStore`). When a flow saved
 * connections:
 * - no draft has input → the dialog closes itself: `onCompletedWithoutInput(via)` (the funnel's `completed_by_import`,
 *   never an abandonment);
 * - a draft has input → the dialog stays, keeps every draft, and shows what was saved (`notice`); a later abandonment
 *   carries `imported_in_session: true` (`onKept`).
 * Cancelling a flow changes nothing: the dialog is still there, in the same view, engine and field, and focus goes back
 * to the entry that opened the flow (`restoreFocus`, called by the flow's dialog as it closes).
 */
import { useEffect, useRef, useState } from 'react';
import { useConnectionDialogHostStore, type NestedFlowCompletion, type NestedFlowVia } from '../../store/connectionDialogHostStore';

type Options = {
  /** Create mode only: an edit dialog offers no entry points. */
  enabled: boolean;
  hasInputNow: () => boolean;
  onCompletedWithoutInput: (via: NestedFlowVia) => void;
  onKept: () => void;
  /** Focus the entry that opened the flow (its dialog is closing). */
  restoreFocus: () => void;
};

export function nestedFlowNotice({ via, count, sourceLabel }: NestedFlowCompletion): string {
  const connections = `${count} ${count === 1 ? 'connection' : 'connections'}`;
  // "it" for one (final UX review UX-16).
  const find = `Find ${count === 1 ? 'it' : 'them'} in Saved Connections.`;
  if (via === 'docker') return `Saved ${count} local database ${count === 1 ? 'connection' : 'connections'}. ${find}`;
  return `Saved ${connections} from ${sourceLabel}. ${find}`;
}

export function useNestedFlows({ enabled, hasInputNow, onCompletedWithoutInput, onKept, restoreFocus }: Options) {
  const [notice, setNotice] = useState<string | null>(null);
  const latest = useRef({ hasInputNow, onCompletedWithoutInput, onKept, restoreFocus });
  latest.current = { hasInputNow, onCompletedWithoutInput, onKept, restoreFocus };

  useEffect(() => {
    if (!enabled) return;
    return useConnectionDialogHostStore.getState().register({
      hasInput: () => latest.current.hasInputNow(),
      completed: (completion) => {
        if (!latest.current.hasInputNow()) {
          latest.current.onCompletedWithoutInput(completion.via);
          return 'closed';
        }
        latest.current.onKept();
        setNotice(nestedFlowNotice(completion));
        return 'kept';
      },
      restoreFocus: () => latest.current.restoreFocus(),
    });
  }, [enabled]);

  return { notice };
}
